import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ScrollText, Eye, RefreshCw, Share2, Sparkles, Link2, Check, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Trainer-facing controls (English) for the EoaE keepsake. Lives in a
// ClientDetailPage tab. The report itself (opened in /eoae/:id) is Spanish.
const EoaEAdminPanel = ({ clientId, clientName }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [trainerMessage, setTrainerMessage] = useState('');
  const [optionalQuote, setOptionalQuote] = useState('');
  const [copied, setCopied] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/eoae/client/${clientId}`);
      setReport(res.data);
      if (res.data) {
        setTrainerMessage(res.data.trainer_message || '');
        setOptionalQuote(res.data.optional_quote || '');
      }
    } catch (e) {
      console.error('Error loading EoaE report:', e);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/eoae/generate`, {
        client_id: clientId,
        trainer_message: trainerMessage || null,
        optional_quote: optionalQuote || null,
      });
      setReport(res.data);
      setShowForm(false);
    } catch (e) {
      console.error('Error generating EoaE:', e);
      alert('Could not generate the report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleShare = async (publicFlag) => {
    if (!report) return;
    // publicFlag: undefined → don't touch public state; true/false → set it
    const qs = publicFlag === undefined ? '' : `?enable_public=${publicFlag ? 'true' : 'false'}`;
    try {
      const res = await axios.post(`${API}/eoae/${report.id}/share${qs}`);
      setReport(res.data);
    } catch (e) {
      console.error('Error sharing EoaE:', e);
      alert('Could not share the report.');
    }
  };

  const publicUrl = report?.public_enabled
    ? `${window.location.origin}/share/eoae/${report.share_token}`
    : null;

  const copyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <div className="text-blue-300 py-8 text-center">Loading…</div>;
  }

  return (
    <div className="space-y-6" data-testid="eoae-admin-panel">
      <div className="flex items-start gap-3">
        <ScrollText className="w-7 h-7 text-amber-400 mt-1" />
        <div>
          <h3 className="text-2xl font-bold text-white">End of an Era</h3>
          <p className="text-blue-300 text-sm mt-1">
            A frozen commemorative keepsake celebrating {clientName}'s coaching journey.
            Generated on demand and never changes afterwards.
          </p>
        </div>
      </div>

      {/* No report yet, or showing the form */}
      {(!report || showForm) && (
        <div className="glass-card p-6 space-y-4" data-testid="eoae-generate-form">
          <div>
            <label className="block text-blue-200 text-sm font-medium mb-2">
              Personal message to the client (shown in the "Legacy" section)
            </label>
            <textarea
              value={trainerMessage}
              onChange={(e) => setTrainerMessage(e.target.value)}
              rows={4}
              className="input-field w-full"
              placeholder="Write a heartfelt closing message…"
              data-testid="eoae-trainer-message"
            />
          </div>
          <div>
            <label className="block text-blue-200 text-sm font-medium mb-2">
              Optional cover quote
            </label>
            <input
              value={optionalQuote}
              onChange={(e) => setOptionalQuote(e.target.value)}
              className="input-field w-full"
              placeholder="A short quote for the cover (optional)"
              data-testid="eoae-quote"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
              data-testid="eoae-generate-btn"
            >
              <Sparkles className="w-4 h-4" />
              {generating ? 'Generating… (writing reflection)' : report ? 'Regenerate Report' : 'Generate EoaE Report'}
            </button>
            {report && (
              <button onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
            )}
          </div>
          {generating && (
            <p className="text-amber-300/80 text-xs">
              This computes every stat, PR and milestone, then writes a personalised reflection with AI. It can take ~10–20s.
            </p>
          )}
        </div>
      )}

      {/* Existing report */}
      {report && !showForm && (
        <div className="glass-card p-6 space-y-5" data-testid="eoae-report-summary">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-white font-semibold">Report ready</p>
              <p className="text-blue-400 text-xs mt-1">
                Snapshot frozen on {new Date(report.generated_at).toLocaleString('en-GB')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={`/eoae/${report.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary flex items-center gap-2 text-sm"
                data-testid="eoae-view-btn"
              >
                <Eye className="w-4 h-4" /> View
              </a>
              <a
                href={`/eoae/${report.id}/print`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary flex items-center gap-2 text-sm"
                data-testid="eoae-export-btn"
              >
                <ExternalLink className="w-4 h-4" /> Export PDF
              </a>
              <button
                onClick={() => setShowForm(true)}
                className="btn-secondary flex items-center gap-2 text-sm"
                data-testid="eoae-regenerate-btn"
              >
                <RefreshCw className="w-4 h-4" /> Regenerate
              </button>
            </div>
          </div>

          {/* Quick snapshot facts */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
            {[
              ['Sessions', report.snapshot?.journey?.total_sessions],
              ['PRs', report.snapshot?.achievements?.pr_count],
              ['Adherence', `${report.snapshot?.journey?.adherence_percent}%`],
              ['Hours', `${report.snapshot?.journey?.hours_trained}h`],
            ].map(([label, val]) => (
              <div key={label} className="bg-white/5 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-white">{val ?? '—'}</p>
                <p className="text-blue-300 text-xs">{label}</p>
              </div>
            ))}
          </div>

          {/* Sharing */}
          <div className="border-t border-white/10 pt-4 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => handleShare(undefined)}
                className={`flex items-center gap-2 text-sm px-4 py-2 rounded-lg ${
                  report.shared_with_client
                    ? 'bg-green-500/20 text-green-200'
                    : 'bg-blue-500/20 text-blue-200 hover:bg-blue-500/30'
                }`}
                data-testid="eoae-share-client-btn"
              >
                {report.shared_with_client ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                {report.shared_with_client ? 'Shared with client' : 'Share with client'}
              </button>
              <button
                onClick={() => handleShare(!report.public_enabled)}
                className={`flex items-center gap-2 text-sm px-4 py-2 rounded-lg ${
                  report.public_enabled
                    ? 'bg-amber-500/30 text-amber-100 hover:bg-amber-500/40'
                    : 'bg-amber-500/20 text-amber-200 hover:bg-amber-500/30'
                }`}
                data-testid="eoae-public-link-btn"
              >
                <Link2 className="w-4 h-4" /> {report.public_enabled ? 'Disable public link' : 'Enable public link'}
              </button>
            </div>
            {report.shared_with_client && (
              <p className="text-green-300/80 text-xs">
                The client sees a banner on their dashboard: "Tu entrenador ha compartido contigo tu informe End of an Era."
              </p>
            )}
            {publicUrl && (
              <div className="flex items-center gap-2 bg-black/30 rounded-lg p-2" data-testid="eoae-public-url-row">
                <input readOnly value={publicUrl} className="flex-1 bg-transparent text-blue-200 text-xs outline-none px-2" />
                <button onClick={copyLink} className="btn-secondary text-xs flex items-center gap-1">
                  {copied ? <Check className="w-3 h-3" /> : <Link2 className="w-3 h-3" />} {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EoaEAdminPanel;
